import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  TemplateRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Bundle, BundleConfiguration, Partner } from '@core/core.types';
import { LoadBundleConfigurationByIds } from '@core/store/bundle-configuration/bundle-configuration.actions';
import { BundleConfigurationsState } from '@core/store/bundle-configuration/bundle-configuration.state';
import {
  AssignBundlesToPartner,
  UnassignBundlesFromPartner,
} from '@core/store/partners/partners.actions';
import { PartnersState } from '@core/store/partners/partners.state';
import { ProductsListState } from '@core/store/products/products.state';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ConfirmService } from '@shared/components/confirm/confirm-dialog.component';
import { DataTableAssignComponent } from '@shared/components/data-table-assign/data-table-assign.component';
import { DataTableAssignConfiguration } from '@shared/components/data-table-assign/data-table-assign.types';
import { ConfirmCloseReason } from '@shared/shared.types';
import { Observable, finalize, map, of, switchMap, take } from 'rxjs';

interface BundleRow {
  bundle: Bundle;
  bundleConfigurationId?: string;
}

@Component({
  selector: 'app-partner-bundle-dialog',
  templateUrl: 'partner-bundle-dialog.component.html',
  styleUrls: ['partner-bundle-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgbTooltip,
    AsyncPipe,
    TranslateModule,
    ReactiveFormsModule,
    DataTableAssignComponent,
  ],
})
export class PartnerBundleDialogComponent implements OnInit {
  private modalRef = inject(NgbActiveModal);
  private store = inject(Store);
  private cdRef = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private confirmService = inject(ConfirmService);

  public partner = signal<Partner>(null);
  protected bundleForm = new FormGroup({
    selectedBundles: new FormControl<BundleRow[]>([], { nonNullable: true }),
  });

  protected bundleAssignConfiguration: DataTableAssignConfiguration;
  protected countryActiveBundleList = this.store.selectSignal(
    ProductsListState.getCountryActiveBundleList
  );

  protected countryBundles = computed(() => {
    const getCountryBundles = this.countryActiveBundleList();
    const country = this.partner().address?.country;

    if (!getCountryBundles || !country) {
      return [];
    }

    return this.sortBundles(getCountryBundles(country));
  });
  protected initialAssignedRows = signal<BundleRow[]>([]);
  protected selectedRows = signal<BundleRow[]>([]);
  protected availableRows = signal<BundleRow[]>([]);
  protected hasPendingChanges = computed(
    () =>
      this.getPendingBundles().length > 0 ||
      this.getRemovedBundleConfigurationIds().length > 0
  );
  protected isLoading = signal(false);

  private assignedBundleNameTemplate = viewChild<TemplateRef<any>>(
    'assignedBundleNameTemplate'
  );
  private availableBundleNameTemplate = viewChild<TemplateRef<any>>(
    'availableBundleNameTemplate'
  );

  ngOnInit(): void {
    this.bundleAssignConfiguration = {
      columnName: 'partnersPage.tableColumns.bundleName',
      leftTable: {
        title: 'partnersPage.bundleForm.availableBundles',
        rows: this.availableRows,
        columnTemplate: this.availableBundleNameTemplate(),
        buttonPermission: 'partners.details.bundleActions',
        search: {
          placeholder: 'partnersPage.bundleForm.searchAvailableBundles',
          property: 'bundle.internalName',
        },
        addAllButton: {
          tooltip: 'partnersPage.tooltips.addAllBundles',
          icon: 'fa-light fa-cubes',
          permission: 'partners.details.bundleActions',
        },
      },
      rightTable: {
        title: 'partnersPage.bundleForm.assignedBundles',
        rows: this.selectedRows,
        columnTemplate: this.assignedBundleNameTemplate(),
        buttonPermission: 'partners.details.bundleActions',
      },
    };

    this.bundleForm.controls.selectedBundles.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rows) => {
        this.selectedRows.set(this.sortBundleRows(rows ?? []));
        this.syncAvailableRows();
        this.cdRef.markForCheck();
      });

    effect(
      () => {
        this.countryBundles();
        this.initialAssignedRows();
        this.syncAvailableRows();
      },
      { injector: this.injector }
    );

    this.loadAssignedBundles();
  }

  protected dismiss(): void {
    if (!this.hasPendingChanges()) {
      this.modalRef.close();
      return;
    }

    this.confirmService
      .confirm({
        title: 'partnersPage.confirmCloseBundleDialogTitle',
        message: 'partnersPage.confirmCloseBundleDialogMessage',
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmOpt) => {
        if (confirmOpt.closeReason === ConfirmCloseReason.Yes) {
          this.modalRef.close();
        }
      });
  }

  protected save(): void {
    if (this.isLoading()) {
      return;
    }

    const pendingBundles = this.getPendingBundles();
    const removedBundleConfigurationIds =
      this.getRemovedBundleConfigurationIds();
    const changedBundlesCount =
      pendingBundles.length + removedBundleConfigurationIds.length;

    if (!changedBundlesCount) {
      this.dismiss();
      return;
    }

    this.isLoading.set(true);

    const actions = [];

    if (pendingBundles.length) {
      actions.push(
        new AssignBundlesToPartner(
          this.partner().id,
          pendingBundles.map((bundle) => bundle.id)
        )
      );
    }

    if (removedBundleConfigurationIds.length) {
      actions.push(
        new UnassignBundlesFromPartner(
          this.partner().id,
          removedBundleConfigurationIds
        )
      );
    }

    this.store
      .dispatch(actions)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.cdRef.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.modalRef.close(changedBundlesCount);
      });
  }

  protected isBundleAvailable(bundleId: string): Observable<boolean> {
    return this.store.select(ProductsListState.getCountryBundleList).pipe(
      map((bundlesByCountry) =>
        bundlesByCountry(
          this.partner().address ? this.partner().address.country : null
        )
      ),
      take(1),
      map(
        (countryBundles: Bundle[]) =>
          !!countryBundles.find((bundle) => bundle.id === bundleId)
      )
    );
  }

  private loadAssignedBundles(): void {
    const bundleConfigurationIds = this.partner().bundleConfigurations ?? [];
    const loadBundleConfigurations$ = bundleConfigurationIds.length
      ? this.store.dispatch(
          new LoadBundleConfigurationByIds(bundleConfigurationIds)
        )
      : of(null);

    loadBundleConfigurations$
      .pipe(
        switchMap(() =>
          this.store.select(PartnersState.getPartnerById).pipe(
            take(1),
            map(
              (getPartnerById) =>
                getPartnerById(this.partner().id)?.bundleConfigurations ??
                this.partner().bundleConfigurations ??
                []
            )
          )
        ),
        switchMap((partnerBundleConfigurationIds: string[]) => {
          if (!partnerBundleConfigurationIds.length) {
            return of([]);
          }

          return this.store
            .select(BundleConfigurationsState.getBundleConfigurationByIds)
            .pipe(
              take(1),
              map((getByIds) =>
                getByIds(partnerBundleConfigurationIds).filter(Boolean)
              ),
              switchMap((bundleConfigurations: BundleConfiguration[]) =>
                this.store.select(ProductsListState.getBundles).pipe(
                  take(1),
                  map((getBundles) =>
                    this.sortBundleConfigurations(
                      bundleConfigurations.map((bundleConfiguration) => ({
                        bundleConfigurationId: bundleConfiguration.id,
                        bundle: getBundles([bundleConfiguration.bundleId])[0],
                      }))
                    )
                  )
                )
              )
            );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((bundleConfigurations) => {
        const assignedRows = this.sortBundleRows(
          bundleConfigurations.map(({ bundle, bundleConfigurationId }) => ({
            bundle,
            bundleConfigurationId,
          }))
        );

        this.initialAssignedRows.set(assignedRows);
        this.selectedRows.set(assignedRows);
        this.bundleForm.controls.selectedBundles.setValue(assignedRows);
        this.cdRef.markForCheck();
      });
  }

  private sortBundles(bundles: Bundle[]): Bundle[] {
    return [...bundles].sort((a, b) =>
      a.internalName.toLowerCase() < b.internalName.toLowerCase() ? -1 : 1
    );
  }

  private sortBundleRows(rows: BundleRow[]): BundleRow[] {
    return [...rows].sort((a, b) => {
      const aIsNew = !a.bundleConfigurationId;
      const bIsNew = !b.bundleConfigurationId;

      if (aIsNew !== bIsNew) {
        return aIsNew ? -1 : 1;
      }

      return a.bundle.internalName.toLowerCase() <
        b.bundle.internalName.toLowerCase()
        ? -1
        : 1;
    });
  }

  private sortBundleConfigurations(
    rows: { bundle: Bundle; bundleConfigurationId: string }[]
  ) {
    return [...rows]
      .filter((row) => !!row.bundle)
      .sort((a, b) =>
        a.bundle.internalName.toLowerCase() <
        b.bundle.internalName.toLowerCase()
          ? -1
          : 1
      );
  }

  private syncAvailableRows(): void {
    const selectedBundleIds = new Set(
      this.selectedRows().map((row) => row.bundle.id)
    );
    const initiallyAssignedByBundleId = new Map(
      this.initialAssignedRows().map((row) => [row.bundle.id, row])
    );

    const nextAvailableRows = this.countryBundles()
      .filter((bundle) => !selectedBundleIds.has(bundle.id))
      .map((bundle) => {
        const initialRow = initiallyAssignedByBundleId.get(bundle.id);

        return {
          bundle,
          bundleConfigurationId: initialRow?.bundleConfigurationId,
        };
      });

    this.availableRows.set(this.sortBundleRows(nextAvailableRows));
  }

  private getPendingBundles(): Bundle[] {
    return this.selectedRows()
      .filter((row) => !row.bundleConfigurationId)
      .map((row) => row.bundle);
  }

  private getRemovedBundleConfigurationIds(): string[] {
    const selectedBundleIds = new Set(
      this.selectedRows().map((row) => row.bundle.id)
    );

    return this.initialAssignedRows()
      .filter((row) => !selectedBundleIds.has(row.bundle.id))
      .map((row) => row.bundleConfigurationId)
      .filter(Boolean);
  }
}
